import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { API } from './api';
import * as moment from 'moment';
import { Utils } from './Utils';

@Injectable()
export class DateAndTime {

  static FORMAT_DATE_API = 'YYYY-MM-DD';
  static FORMAT_DATE_CALENDAR = 'YYYY-MM-DD';
  static FORMAT_DATE_DISPLAY = 'DD/MM/YYYY';
  public static FORMAT_DATE_DISPLAY_EXPENSES = 'dd/mm/yyyy';
  static FORMAT_DATE_NOT_TODAY_HUMANIZE = 'dddd, DD [de] MMMM';
  FORMAT_DATE_TODAY_HUMANIZE = '[' + this.translate.instant('Menus.today') + '], DD [de] MMMM';

  constructor(public api: API,
              public translate: TranslateService,
              private utils: Utils,
              ) { }

  formatDateFromDisplayToAPI(value:string) : string {
    if (!value || value == '') {
      return null;
    }
    return moment(value, DateAndTime.FORMAT_DATE_DISPLAY).format(DateAndTime.FORMAT_DATE_API);
  }

  formatDateFromAPItoDisplay(value:string) : string {
    return moment(value, DateAndTime.FORMAT_DATE_API).format(DateAndTime.FORMAT_DATE_DISPLAY);
  }

  formatDateFromDisplayToCalendar(value:string) : string {
    let mom;
    if (!value || value == '') {
      mom = moment(new Date()); // for now.
    } else {
      mom = moment(value, DateAndTime.FORMAT_DATE_DISPLAY);
    }
    return mom.format(DateAndTime.FORMAT_DATE_CALENDAR);
  }

  formatDateFromCalendarToDisplay(value:string) : string {
    return moment(value, DateAndTime.FORMAT_DATE_CALENDAR).format(DateAndTime.FORMAT_DATE_DISPLAY);
  }

  formatDateFromCalendarToAPI(value:string) : string {
    return moment(value, DateAndTime.FORMAT_DATE_CALENDAR).format(DateAndTime.FORMAT_DATE_API);
  }

  formatDateFromAPIToCalendar(value:string) : string {
    return moment(value, DateAndTime.FORMAT_DATE_API).format(DateAndTime.FORMAT_DATE_CALENDAR);
  }

  formatDateFromAPIToHumanize(dateFormatAPI:string) : string {
    if (this.isTodayFormatAPI(dateFormatAPI)) {
      return moment(dateFormatAPI).format(this.FORMAT_DATE_TODAY_HUMANIZE);
    } else {
      return moment(dateFormatAPI).format(DateAndTime.FORMAT_DATE_NOT_TODAY_HUMANIZE);
    }
  }

  getTodayHumanized() : string {
    return moment().format(this.FORMAT_DATE_TODAY_HUMANIZE);
  }

  getTodayFormatAPI() : string {
    return moment().format('YYYY-MM-DD');
  }

  // Añade un "0" delante en "time" si tiene la forma 'h:mm' en lugar de 'hh:mm'.
  padStartZeroInHours(time:string, numDigitsHours = 2) : string {
    if (time && time != '') {
      const part = time.replace(/\_/g, '').split(':'); // quitamos '_' y separamos en horas y minutos.
      let hh = part[0];
      let mm = (part[1]) ? part[1] : '00';

      hh = hh.padStart(numDigitsHours, '0'); // pone '0's delante si hace falta.
      mm = mm.padEnd(2, '0'); // pone '0's al final de los minutos si user puso :m en lugar de :mm.

      return `${hh}:${mm}`;
    }
    return null;
  }

  timeMaskHHMM(value: string) {
    const chars = value.split('');
    let hours;
    if (chars[0] == '0' || chars[0] == '1' || chars[0] == '2') {
      if (chars[1] == ':') {
        // only one digit before the colon: "0", "1", "2"
        hours = [/[0-2]/];
      } else {
        // two digits: 00, 01, 02, 03, 04, ..., 18, 19, 20, 21, 22, 23
        hours = [/[0-2]/, chars[0] == '2' ? /[0-3]/ : /[0-9]/];
      }
    } else {
      // one digit greater than two: 3, 4, 5, 6, 7, 8, 9
      hours = [/[3-9]/];
    }
    // minutes is always two-digits
    const minutes = [/[0-5]/, /[0-9]/];
    return hours.concat(':').concat(minutes);
  }

  timeMaskHHHHMM(value) {
    let hours;
    hours = [/[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/]; // HHHH
    // minutes is always two-digits
    const minutes = [/[0-5]/, /[0-9]/]; // MM

    return hours.concat(':').concat(minutes);
  }

  // TODO: mejorar el cálculo de comparación de horas
  // (código heredado de work-day-record)
  isGreaterHourThan(timeA:any, timeB:any) : boolean {
    const aHours = timeA.split(':')[0];
    const aMin = timeA.split(':')[1];

    const bHours = timeB.split(':')[0];
    const bMin = timeB.split(':')[1];

    const a = moment(new Date().setHours(aHours, aMin)).local().format('HH:mm');
    const b = moment(new Date().setHours(bHours, bMin)).local().format('HH:mm');

    return (a > b);
  }

  // Calculate difference between 2 HH:mm. Show the result in diffCtrl control.
  methodRestHours(startHourCtrl: AbstractControl, endHourCtrl: AbstractControl,
                  diffCtrl: AbstractControl, effectiveTimeCtrl: AbstractControl) {
    if (startHourCtrl.value && endHourCtrl.value) {
      const fromTime = moment(startHourCtrl.value, 'HH:mm');
      const toTime = moment(endHourCtrl.value, 'HH:mm');
      const duration = moment.duration(toTime.diff(fromTime));

      if (duration.asMinutes() >= 0) {
        const horas = duration.hours().toString().padStart(2, '0'); // requires "es2017.string"
        const minutos = duration.minutes().toString().padStart(2, '0');

        diffCtrl.setValue(`${horas}:${minutos}`);
        if (this.utils.getIsApp()) {
          effectiveTimeCtrl.enable();
        }
      } else {
        diffCtrl.setValue(this.translate.instant('WorkDayRecord.errorOut'));
        effectiveTimeCtrl.setValue('');
        if (this.utils.getIsApp()) {
          effectiveTimeCtrl.disable();
        }
      }

      /*
      // implementación alternativa, sin librería moment.

      const inicioHoras = startHourCtrl.value.split(':')[0];
      const inicioMinutos = startHourCtrl.value.split(':')[1];

      const finHoras = endHourCtrl.value.split(':')[0];
      const finMinutos = endHourCtrl.value.split(':')[1];

      let transcurridoMinutos: any = finMinutos - inicioMinutos;
      let transcurridoHoras: any = finHoras - inicioHoras;

      if (transcurridoMinutos < 0) {
        transcurridoHoras--;
        transcurridoMinutos = 60 + transcurridoMinutos;
      }

      if (transcurridoHoras < 0 || transcurridoMinutos < 0) {
        diffCtrl.setValue(this.translate.instant('WorkDayRecord.errorOut'));
        effectiveTimeCtrl.setValue('');
        if (this.utils.getIsApp()) {
          effectiveTimeCtrl.disable();
        }
      } else {
        const horas = transcurridoHoras.toString().padStart(2, '0'); // requires "es2017.string"
        const minutos = transcurridoMinutos.toString().padStart(2, '0');

        diffCtrl.setValue(`${horas}:${minutos}`);
        if (this.utils.getIsApp()) {
          effectiveTimeCtrl.enable();
        }
      }
      */
    }
  }

  // TODO: mejorar esta comparación de fechas, para que funcione aunque cambiase el formato de fecha.
  isTodayFormatAPI(dateString: string) : boolean {
    return dateString == moment(new Date().toISOString()).format(DateAndTime.FORMAT_DATE_API);
  }

  isTodayOrFutureFormatAPI(dateString: string) : boolean {
    return dateString >= moment(new Date().toISOString()).format(DateAndTime.FORMAT_DATE_API);
  }

  isPastDateFormatAPI(dateString: string) : boolean {
    return dateString < moment(new Date().toISOString()).format(DateAndTime.FORMAT_DATE_API);
  }

  getPreviousOrNextFormatAPI(dateFormatAPI: string, direction: DateAndTime.CalendarArrow) : string {
    const format = moment(new Date(dateFormatAPI).toISOString());
    let datePreviousNext: moment.Moment;

    if (direction == DateAndTime.CalendarArrow.LEFT) {
      datePreviousNext = format.subtract(1, 'day');
    } else {
      datePreviousNext = format.add(1, 'day');
    }

    return datePreviousNext.format(DateAndTime.FORMAT_DATE_API);
  }
}
export namespace DateAndTime {
  export enum CalendarPickMode {
    SINGLE,
    RANGE,
  }
}

export namespace DateAndTime {
  export enum RangeLimit {
    FROM,
    TO,
  }
}

export namespace DateAndTime {
  export enum CalendarArrow {
    LEFT,
    RIGHT,
  }
}



// WEBPACK FOOTER //
// ./src/services/dateAndTime.service.ts